import i18n from 'i18next';

import {apiClient, updateTokensBeforeExpire} from 'core/apiClient';
import {IS_LOCALHOST} from 'core/constants/env';
import {DocumentReportType, GdsStatus} from 'core/dictionaries';
import {
    BlockingAccessors,
    DocumentDetail,
    DocumentsAccessors,
    DocumentsGridRawRow,
    MovingGridRawRow,
    ProductContentRawRequest,
    ProductContentRawResponse,
    WithdrawalDictionary,
    WithdrawalGridRawRow,
} from 'types';
import {
    ApiFilters,
    ApiPaging,
    ApiSortings,
    SimplePaginationContent,
    SimplePaginationContentRaw,
} from 'types/api';
import {ProductsUPDItem} from 'types/documents';
import {InvoiceFiltersState} from 'types/invoices';
import {
    ProductBlockingCodesResponseData,
    ProductBlockingTableDictionary,
    ProductBlockingTableFilterValue,
    ProductBlockingTableRequest,
    ProductBlockingTableResponseData,
} from 'types/products-blocking';

const DOCUMENTS_URL = '/service/documents';
const DOCUMENTS_URL_MARKING = '/service/documents/custom';
const MOVING_URL = '/service/moving';
const WITHDRAWAL_URL = '/service/dropout';
const BLOCKING_URL = '/service/blocking';

let gridTokensCache: Record<number, string | null> = {
    0: null,
};

export function clearDocumentsGridCache(): void {
    gridTokensCache = {
        0: null,
    };
}

const filtersMap = new Map<string, [string]>([
    ['/invoices/incoming', ['INCOMING']],
    ['/invoices/outgoing', ['OUTGOING']],
    ['/documents/transfer/incoming', ['INCOMING']],
    ['/documents/transfer/outgoing', ['OUTGOING']],
]);

export function modifyParams(params: {
    sortings: ApiSortings;
    filters: ApiFilters;
    paging: ApiPaging;
    search: string;
    updOnly?: boolean;
    flatFilters?: string[];
}) {
    params.flatFilters?.forEach((filterId) => {
        const filter = params.filters.find((filter) => filter.id === filterId);
        if (filter && Array.isArray(filter?.value)) {
            const flatFilter = filter.value[0];
            if (filter?.value?.length) {
                // @ts-ignore
                filter.value = flatFilter;
            }
        }
    });

    delete params.flatFilters;

    const createdDateRange = params.filters.find(
        (filter) => filter.id === DocumentsAccessors.createdDate,
    );
    if (createdDateRange && Array.isArray(createdDateRange?.value)) {
        const createdDateRangeFlattered = createdDateRange.value[0];
        if (createdDateRange?.value?.length) {
            // @ts-ignore
            createdDateRange.value = createdDateRangeFlattered;
        }
    }

    if (params.updOnly) {
        const newFilter = {
            id: DocumentsAccessors.side,
            value: filtersMap.get(location.pathname) ?? [],
        };
        params.filters.push(newFilter);
        const documentType = location.pathname.startsWith('/invoices/')
            ? DocumentReportType.UPD
            : location.pathname.startsWith('/documents/transfer/')
            ? DocumentReportType.TRANSFER_DOCUMENT
            : null;

        if (documentType) {
            const typeFilter = params.filters.find(
                (filter) => filter.id === DocumentsAccessors.type,
            );
            if (typeFilter) {
                typeFilter.value = [documentType];
            } else {
                params.filters.push({
                    id: DocumentsAccessors.type,
                    value: [documentType],
                });
            }
        }
    } else {
        const typeFilter = params.filters.find((filter) => filter.id === DocumentsAccessors.type);
        if (typeFilter?.value?.length) {
            const [value] = typeFilter?.value;
            //@ts-ignore
            params.type = value;
        }
    }

    if (
        !location.pathname.startsWith('/invoices/') &&
        !location.pathname.startsWith('/documents/transfer/')
    ) {
        const createdAt = params.filters.find(
            (filter) => filter.id === DocumentsAccessors.createdDate,
        );
        if (createdAt?.value?.length) {
            const [value] = createdAt?.value;
            //@ts-ignore
            const date = new Date(value);
            date.setUTCMinutes(date.getUTCMinutes() - date.getTimezoneOffset());
            //@ts-ignore
            params.createdAt = date.toISOString();
        }
    }

    if (params.search) {
        //@ts-ignore
        params.docOrOwner = params.search;
    }
    //@ts-ignore
    delete params.search;
}
interface FetchParamsType {
    sortings: ApiSortings;
    filters: ApiFilters;
    paging: ApiPaging;
    search: string;
    updOnly?: boolean;
    pageRef?: string | null;
}

export async function fetchDocumentsGrid(
    params: FetchParamsType,
): Promise<SimplePaginationContent<DocumentsGridRawRow>> {
    const EMPTY_DATA = {
        content: [],
        hasNext: false,
        nextPageRef: '',
    };
    if (location.pathname === '/invoices') {
        return EMPTY_DATA;
    }
    const {pageRef, ...restParams} = params;
    modifyParams(restParams);
    const {pageSize, pageNumber} = restParams.paging;
    const mappedPaging = pageNumber === 0 ? {pageSize} : {pageRef};
    const responseData: SimplePaginationContentRaw<DocumentsGridRawRow> = await apiClient
        .post(DOCUMENTS_URL_MARKING, {
            ...restParams,
            paging: mappedPaging,
        })
        .then((res) => res.data)
        .catch(() => EMPTY_DATA);

    return {
        content: responseData.data,
        hasNext: !!responseData.nextPageRef,
        nextPageRef: responseData.nextPageRef,
    };
}

export async function fetchMovingGrid(
    params: FetchParamsType,
): Promise<SimplePaginationContent<MovingGridRawRow>> {
    const {pageRef, ...restParams} = params;
    modifyParams(
        Object.assign(restParams, {
            flatFilters: ['storageLocationFrom', 'storageLocationTo', 'employeeFullName', 'tin'],
        }),
    );
    const {pageSize, pageNumber} = restParams.paging;
    const mappedPaging = pageNumber === 0 ? {pageSize} : {pageRef};
    const responseData: SimplePaginationContentRaw<MovingGridRawRow> = (
        await apiClient.post(MOVING_URL, {...restParams, paging: mappedPaging})
    ).data;

    return {
        content: responseData.data,
        hasNext: !!responseData.nextPageRef,
        nextPageRef: responseData.nextPageRef,
    };
}

export async function fetchWithdrawalGrid(
    params: FetchParamsType,
): Promise<SimplePaginationContent<WithdrawalGridRawRow>> {
    const {pageRef, ...request} = params;
    modifyParams(
        Object.assign(request, {
            flatFilters: [
                'reason',
                'supportingDocumentNumber',
                'supportingDocumentDate',
                'documentIssuer',
            ],
        }),
    );
    const {pageSize, pageNumber} = request.paging;
    const paging = pageNumber === 0 ? {pageSize} : {pageRef};
    const responseData: SimplePaginationContentRaw<WithdrawalGridRawRow> = (
        await apiClient.post(WITHDRAWAL_URL, {...request, paging})
    ).data;

    return {
        content: responseData.data,
        hasNext: !!responseData.nextPageRef,
        nextPageRef: responseData.nextPageRef,
    };
}

export async function fetchWithdrawalDictionary(
    params: FetchParamsType,
): Promise<WithdrawalDictionary> {
    const request = {...params};
    delete request.pageRef;
    modifyParams(
        Object.assign(request, {
            flatFilters: [
                'reason',
                'supportingDocumentNumber',
                'supportingDocumentDate',
                'documentIssuer',
            ],
        }),
    );
    return (await apiClient.post<WithdrawalDictionary>(`${WITHDRAWAL_URL}/dict`, request)).data;
}

export async function fetchDocumentsDetail(id: string): Promise<DocumentDetail> {
    return (await apiClient.post(`${DOCUMENTS_URL}/details`, {reportId: id})).data;
}

export async function downloadDocumentFile(documentNumber: string, fileName: string) {
    return (
        await apiClient.get<Blob>(
            `/marking/documents/file/download/${encodeURIComponent(
                documentNumber,
            )}/${encodeURIComponent(fileName)}`,
            {baseURL: '/api/v2', responseType: 'blob'},
        )
    ).data;
}

export async function fetchInvoiceDetail(id: string | null): Promise<string> {
    if (!id) {
        return '';
    }
    const {data} = await apiClient.get(`/marking/invoice/body`, {
        params: {reportId: id},
        baseURL: '/api/v2',
    });

    if (data) {
        return data;
    }

    throw new Error('File is Empty');
}

export async function fetchTinLegalName(params?: {
    combinedSearch: string;
}): Promise<
    {
        city: string;
        country: string;
        legalName: string;
        postalCode: string;
        street: string;
        taxIdNumber: string;
    }[]
> {
    const {data} = await apiClient.get(`/service/dict/tin-legal-name`, {
        params,
    });

    return data ?? [];
}

export async function fetchStorageLocation(
    taxIdNumber: string,
): Promise<
    {
        storageName: string;
        address: string;
    }[]
> {
    const {data} = await apiClient.get(`service/dict/storage-location`, {
        params: {
            taxIdNumber,
        },
    });

    return data ?? '';
}

export async function deleteInvoice(invoiceId: string): Promise<any> {
    if (!invoiceId) {
        return;
    }

    const {data} = await apiClient.post(`/marking/invoice/delete?reportId=${invoiceId}`);

    return {
        data,
    };
}

export async function updateInvoiceStatus(params: {
    invoiceId: string;
    status: string;
}): Promise<any> {
    if (!params.invoiceId || !params.status) {
        return;
    }

    const {
        data,
    } = await apiClient.post(
        `/marking/invoice/update-status?reportId=${params.invoiceId}&reportStatus=${params.status}`,
        null,
        {baseURL: '/api/v2'},
    );

    return {data};
}

export async function updateInvoiceBody(params: {reportId: string; body: string}): Promise<any> {
    const formData = new FormData();
    const file = new Blob([params.body], {type: 'application/xml'});

    formData.append('file', file, 'invoice.xml');

    const data: any = await apiClient.post(
        `/marking/invoice/update-body?reportId=${params.reportId}`,
        formData,
        {baseURL: '/api/v2'},
    );
    return {data};
}

export async function fetchDocumentProductsGrid(params: ProductContentRawRequest) {
    const res = (
        await apiClient.post<ProductContentRawResponse>(`${DOCUMENTS_URL}/details/products`, params)
    ).data;

    return {
        nextPageRef: res.nextPageRef,
        data: res.data,
    };
}

export async function fetchInvoicesFilterState(params: FetchParamsType) {
    const {pageRef, ...restParams} = params;
    modifyParams(restParams);
    const {data} = await apiClient.post<InvoiceFiltersState>(
        `${DOCUMENTS_URL_MARKING}/dict`,
        restParams,
    );
    return data;
}

export async function fetchUPDProductsGrid(params: {
    reportId: string;
    paging: ApiPaging;
    sortings: ApiSortings;
    filters: ApiFilters;
}): Promise<ProductsUPDItem[]> {
    return (await apiClient.post(`${DOCUMENTS_URL}/details/upd-products`, params))
        .data as ProductsUPDItem[];
}

export async function downloadInvoicePDF(id?: string) {
    await updateTokensBeforeExpire();
    if (!id) throw Error('No ID');
    const path =
        (IS_LOCALHOST
            ? 'https://lk-participant-refdev.pmark.tracecore.solutions'
            : window.location.origin) + `/invoices/print/${id}?lang=${i18n.language}`;

    return (
        await apiClient.post<Blob>('/pdf', {url: path}, {baseURL: '/api', responseType: 'blob'})
    ).data;
}

interface FetchGdsGridParams extends Omit<FetchParamsType, 'updOnly' | 'search' | 'pageRef'> {
    searchField: string;
}
export async function fetchGdsGrid(params: FetchGdsGridParams): Promise<any> {
    const {filters, ...restParams} = params;
    const updatedFilters = filters.map((filter) => {
        if (filter.id === 'receiptedAt') {
            return {
                id: filter.id,
                value: filter.value[0],
            };
        }
        return filter;
    });
    const updatedParams = {
        ...restParams,
        filters: updatedFilters,
        // paging: {
        //     pageSize: paging.pageSize,
        //     pageNumber: paging.pageNumber + 1,
        // },
    };
    return (await apiClient.post('/customs/declaration/page', updatedParams)).data;
}

export interface GoodsDeclarationDetails {
    id: string;
    participantName: string;
    participantTin: string;
    storageLocation: string;
    receiptedAt: string;
    status: GdsStatus;
    gdNumber: string | null;
    customsMarkingCodeList: {
        code: string;
        storageLocation?: string;
        updatedAt?: string;
        objectsNumber?: number;
        status?: string;
    }[];
    customsId: number | string;
    legalIdentityId: number;
}
export async function fetchGdsDetail(id: string): Promise<GoodsDeclarationDetails> {
    return (await apiClient.get(`/customs/declaration/${id}`)).data;
}

export async function createGds(params: {
    customsId: number;
    storageLocation: string;
    markingCodeList: string[];
}): Promise<string> {
    return (await apiClient.post('/customs/declaration', params)).data;
}

export async function saveChangesGdsInfo(
    id: string,
    params: {
        customsId: number;
        storageLocation: string;
    },
): Promise<string> {
    return (await apiClient.put(`/customs/declaration/${id}`, params)).data;
}

export async function saveChangesGdsCodeList(
    id: string,
    params: {
        [key: string]: boolean;
    },
): Promise<string> {
    return (await apiClient.put(`/customs/declaration/codes/${id}`, params)).data;
}

export async function submitGds(id: string): Promise<string> {
    return (await apiClient.put(`/customs/declaration/submit/${id}`)).data;
}

export async function getCustoms(): Promise<
    {
        address: string;
        id: number;
        name: string;
        number: string;
    }[]
> {
    const data = await apiClient.post('/customs/page', {});
    return data.data.data;
}

const flattenBlockingFilterValue = (
    value: ApiFilters[number]['value'],
): ProductBlockingTableFilterValue | null => {
    const raw = Array.isArray(value) ? value[0] : value;
    if (raw == null) {
        return null;
    }
    return (typeof raw === 'object' ? raw : String(raw)) as ProductBlockingTableFilterValue;
};

const mapBlockingTableFilters = (
    filters: ApiFilters,
    search?: string,
): ProductBlockingTableRequest['filters'] => {
    const mappedFilters = filters.reduce<ProductBlockingTableRequest['filters']>(
        (result, filter) => {
            const value = flattenBlockingFilterValue(filter.value);
            if (value != null) {
                result.push({id: filter.id, value});
            }
            return result;
        },
        [],
    );

    if (search) {
        mappedFilters.push({id: BlockingAccessors.id, value: search});
    }

    return mappedFilters;
};

export const toProductBlockingTableRequest = (
    params: FetchParamsType,
): ProductBlockingTableRequest => {
    const {pageRef, search, paging, filters, sortings} = params;

    return {
        paging: {
            pageSize: paging.pageSize,
            pageRef: pageRef ?? null,
        },
        filters: mapBlockingTableFilters(filters, search),
        sortings: sortings as ProductBlockingTableRequest['sortings'],
    };
};

const DEFAULT_BLOCKING_DICTIONARY_PAGING = {
    pageSize: 20,
    pageRef: null,
};

const DEFAULT_BLOCKING_DICTIONARY_SORTINGS: ProductBlockingTableRequest['sortings'] = [
    {id: BlockingAccessors.createdDate, desc: true},
];

export const toProductBlockingDictionaryRequest = (
    params: FetchParamsType,
): ProductBlockingTableRequest => {
    const createdAt = params.filters.find((filter) => filter.id === BlockingAccessors.createdDate);
    const dateFilter = createdAt ? flattenBlockingFilterValue(createdAt.value) : null;

    return {
        paging: DEFAULT_BLOCKING_DICTIONARY_PAGING,
        sortings: DEFAULT_BLOCKING_DICTIONARY_SORTINGS,
        filters: dateFilter != null ? [{id: BlockingAccessors.createdDate, value: dateFilter}] : [],
    };
};

export async function fetchProductBlockingTable(
    params: FetchParamsType,
): Promise<SimplePaginationContent<ProductBlockingCodesResponseData>> {
    const responseData: ProductBlockingTableResponseData = (
        await apiClient.post(BLOCKING_URL, toProductBlockingTableRequest(params))
    ).data;

    return {
        content: responseData.data,
        hasNext: !!responseData.nextPageRef,
        nextPageRef: responseData.nextPageRef,
    };
}

export async function fetchBlockingTableDictionary(
    params: ProductBlockingTableRequest,
): Promise<ProductBlockingTableDictionary> {
    return (await apiClient.post<ProductBlockingTableDictionary>(`${BLOCKING_URL}/dict`, params))
        .data;
}
